import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { GridCloseIcon } from '@mui/x-data-grid'
import { Error as ErrorIcon } from '@mui/icons-material'

interface Props {
  open: boolean
  setOpen: (op: boolean) => void
  maxSize: string
  maxDuration: string
  setVideoData: (videoData: { file: File; url: string; duration: number }) => void
  error: string
  setError: (e: string) => void
}

const DialogUploadVideo: React.FC<Props> = ({
  open,
  setOpen,
  maxDuration,
  maxSize,
  setVideoData,
  error,
  setError
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [validating, setValidating] = useState(false)

  // Convertir maxSize y maxDuration a números
  const maxSizeInMB = parseFloat(maxSize)
  const maxDurationInSeconds = parseFloat(maxDuration) * 60 // Asumiendo que maxDuration está en minutos

  const validateVideo = (file: File): Promise<{ valid: boolean; error?: string; duration?: number }> => {
    return new Promise((resolve) => {
      // Validar tipo de archivo
      if (!file.type.startsWith('video/')) {
        resolve({ valid: false, error: 'El archivo debe ser un video' })
        return
      }

      // Validar tamaño
      const fileSizeInMB = file.size / (1024 * 1024)
      if (fileSizeInMB > maxSizeInMB) {
        resolve({ valid: false, error: `El video supera el tamaño máximo de ${maxSize}MB` })
        return
      }

      // Validar duración
      const video = document.createElement('video')
      video.preload = 'metadata'

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src)
        const duration = video.duration

        if (duration > maxDurationInSeconds) {
          resolve({
            valid: false,
            error: `El video supera la duración máxima de ${maxDuration} minutos`
          })
        } else {
          resolve({ valid: true, duration })
        }
      }

      video.onerror = () => {
        resolve({ valid: false, error: 'Error al leer el video' })
      }

      video.src = URL.createObjectURL(file)
    })
  }

  const handleVideoSelection = async (file: File) => {
    setError('')
    setValidating(true)

    // Validar el video
    const validation = await validateVideo(file)

    if (!validation.valid) {
      setError(validation.error || 'Error en la validación del video')
      setValidating(false)
      return
    }

    const videoUrl = URL.createObjectURL(file)

    setVideoData({
      file: file,
      url: videoUrl,
      duration: validation.duration || 0
    })

    setOpen(false)
    resetState()
  }

  const resetState = () => {
    setError('')
    setValidating(false)
  }

  const handleGalleryClick = () => {
    fileInputRef.current?.click()
  }

  const handleRecordClick = () => {
    videoInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleVideoSelection(file)
    }
    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    event.target.value = ''
  }

  const handleClose = () => {
    if (!validating) {
      setOpen(false)
      resetState()
    }
  }

  useEffect(() => {
    if (open && !isMobile) {
      fileInputRef.current?.click()
      setOpen(false)
    }
  }, [open, isMobile])

  return (
    <>
      <input ref={fileInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleFileChange} />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {isMobile && (
        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: '24px',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%'
            }
          }}
        >
          <IconButton
            onClick={handleClose}
            disabled={validating}
            sx={{
              position: 'absolute',
              top: '0.5rem',
              right: '0.5rem',
              color: '#666'
            }}
          >
            <GridCloseIcon />
          </IconButton>

          <DialogTitle
            sx={{
              padding: 0,
              marginBottom: '24px',
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                fontSize: '28px',
                lineHeight: '1.3',
                color: '#000'
              }}
            >
              {validating ? 'Validando video...' : 'Elegí desde dónde querés subir el video'}
            </Typography>
          </DialogTitle>

          <DialogContent
            sx={{
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            {!validating && (
              <>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleGalleryClick}
                  disabled={validating}
                  sx={{
                    backgroundColor: '#5B7FFF',
                    color: 'white',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    fontSize: '15px',
                    padding: '16px',
                    borderRadius: '24px',
                    '&:hover': {
                      backgroundColor: '#4A6EEE'
                    }
                  }}
                >
                  Elegir desde la galería
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleRecordClick}
                  disabled={validating}
                  sx={{
                    backgroundColor: '#5B7FFF',
                    color: 'white',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    fontSize: '15px',
                    padding: '16px',
                    borderRadius: '24px',
                    '&:hover': {
                      backgroundColor: '#4A6EEE'
                    }
                  }}
                >
                  Hacer un video
                </Button>
              </>
            )}

            {validating && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#666'
                  }}
                >
                  Validando archivo...
                </Typography>
              </Box>
            )}

            {error && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: '#ffebee',
                  padding: '12px',
                  borderRadius: '12px',
                  mt: 2
                }}
              >
                <ErrorIcon sx={{ color: '#d32f2f', fontSize: 24 }} />
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#d32f2f'
                  }}
                >
                  {error}
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default DialogUploadVideo
